import { useStore } from '@/stores/useStore';
import type { TFunction } from 'i18next';
import type { ManualDesignPoint } from '../../-data/types';

/**
 * Resolves the manual DP currently under edit from the draft store, if any.
 * Shared by every surface that reflects the focused-DP state — the sidebar
 * banner (StudyPreview), the solver-form hint (SolverSettingsForm), and the
 * top-bar chip (StudyHeader) — so all three read one lookup instead of
 * re-implementing the `find(e => e.id === editingId)` pattern.
 *
 * Returns `null` when nothing is under edit, or when the edited id has been
 * removed from the list since it was set (the slice already auto-clears on
 * removal — this is a defensive `null` for the race window between the
 * remove and the re-render).
 */
export function useEditingManualDp(): ManualDesignPoint | null {
  const editingId = useStore((s) => s.editingManualDpId);
  return useStore((s) =>
    editingId
      ? (s.draftConfig.extra_design_points.find((e) => e.id === editingId) ??
        null)
      : null,
  );
}

/**
 * `useEditingManualDp` plus the derived display fields every consumer
 * needs — the pill/banner label (user-authored tokens or the localized
 * "manual" fallback) and the DP's color stripe. Kept as a small hook
 * so the three consumers stay one-liners.
 */
export function useEditingManualDpDisplay(
  t: TFunction,
): { editing: ManualDesignPoint; label: string; stripe: string } | null {
  const editing = useEditingManualDp();
  if (!editing) return null;
  const label =
    editing.label && editing.label.trim() !== ''
      ? editing.label
      : t('simopsDraft.studyPreview.tagManual');
  const stripe = editing.color ?? 'var(--chart-1)';
  return { editing, label, stripe };
}

/**
 * Full-status resolver used by the sidebar banner, solver-form banner, and
 * top-bar chip. Returns which mode the draft form is in — 'editing' when
 * a manual DP is focused, 'viewing' as the default authoring mode where
 * every field edit lands on the study-wide defaults. The `label` +
 * `stripe` mirror `useEditingManualDpDisplay` for the editing case; on
 * viewing the label is the localized "study defaults" line and the stripe
 * is the neutral primary token.
 */
export function useDraftFormStatus(t: TFunction): {
  mode: 'editing' | 'multi' | 'viewing';
  label: string;
  stripe: string;
  editing: ManualDesignPoint | null;
  /** Manual DP multi-select count from the store — non-zero triggers
   *  the orbiting-outline "N DP selected" chip across every surface. */
  multiCount: number;
} {
  const editing = useEditingManualDp();
  const multiCount = useStore((s) => s.selectedManualDpIds.size);
  // Multi mode wins over editing when a selection exists — Ctrl+click
  // on additional rows while a single DP was under edit reads as "user
  // is building a selection to delete", not "still editing DP A".
  // Without this, the status chip stays stuck on the first DP's label
  // while the row tint shows N selected → mismatch.
  if (multiCount > 0) {
    return {
      mode: 'multi',
      label: t('simopsDraft.studyPreview.multiSelectStatusChip', {
        count: multiCount,
      }),
      // Destructive tone matches the row tint + Delete action affordance.
      stripe: 'var(--destructive)',
      editing: null,
      multiCount,
    };
  }
  if (editing) {
    const label =
      editing.label && editing.label.trim() !== ''
        ? editing.label
        : t('simopsDraft.studyPreview.tagManual');
    return {
      mode: 'editing',
      label,
      stripe: editing.color ?? 'var(--chart-1)',
      editing,
      multiCount,
    };
  }
  // No "viewing-row" branch — a sweep-row focus doesn't change what fields
  // edit (still study defaults), so it must not read as a distinct mode
  // in the status chip / banner. Row highlight lives on the row itself
  // (orange outline + heatmap-cell selection), not the mode banner.
  return {
    mode: 'viewing',
    label: t('simopsDraft.studyPreview.viewingStudyDefaults'),
    stripe: 'var(--muted-foreground)',
    editing: null,
    multiCount,
  };
}
